import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  AlertTriangle, AlertOctagon, Flame, Info, Folder, Clock, DollarSign,
  Search, ArrowRight,
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

const GRAV_COLOR = {
  critique: "#C0392B",
  grave: "#E67E22",
  moderee: "#D4A017",
  mineure: "#6B7280",
};
const GRAV_LABEL = {
  critique: "Critique",
  grave: "Grave",
  moderee: "Modérée",
  mineure: "Mineure",
};
const PRIO_LABEL = {
  urgent: "Urgent",
  court_terme: "Court terme",
  moyen_terme: "Moyen terme",
  long_terme: "Long terme",
};

export default function Diagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    gravite: "all",
    priorite: "all",
    country: "all",
    project: "all",
    q: "",
  });

  useEffect(() => {
    api.get("/diagnostics/summary")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return (data.fiches || []).filter((f) => {
      if (filters.gravite !== "all" && f.gravite !== filters.gravite) return false;
      if (filters.priorite !== "all" && f.priorite !== filters.priorite) return false;
      if (filters.country !== "all" && (f.project_country || "Inconnu") !== filters.country) return false;
      if (filters.project !== "all" && f.project_id !== filters.project) return false;
      if (filters.q) {
        const q = filters.q.toLowerCase();
        const blob = (
          (f.anomalie || "") +
          " " +
          (f.qualification_juridique || "") +
          " " +
          (f.project_name || "")
        ).toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [data, filters]);

  const projects = useMemo(() => {
    if (!data) return [];
    const m = new Map();
    (data.fiches || []).forEach((f) => {
      if (!m.has(f.project_id)) {
        m.set(f.project_id, { id: f.project_id, name: f.project_name });
      }
    });
    return [...m.values()];
  }, [data]);

  if (loading || !data) {
    return (
      <div className="p-6 text-sm opacity-60" data-testid="diagnostics-page-loading">
        Chargement des diagnostics…
      </div>
    );
  }

  const t = data.totals || {};
  const gravitePie = [
    { name: "Critique", value: data.by_gravite?.critique || 0, key: "critique" },
    { name: "Grave", value: data.by_gravite?.grave || 0, key: "grave" },
    { name: "Modérée", value: data.by_gravite?.moderee || 0, key: "moderee" },
    { name: "Mineure", value: data.by_gravite?.mineure || 0, key: "mineure" },
  ].filter((x) => x.value > 0);

  const isEmpty = (t.fiches || 0) === 0;

  return (
    <div className="p-6 space-y-6" data-testid="diagnostics-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
          <AlertTriangle size={28} style={{ color: "#D4A017" }} />
          Diagnostics
        </h1>
        <p className="text-sm opacity-70 mt-1">
          Toutes les fiches diagnostic générées sur l'ensemble de vos projets,
          filtrables par gravité, priorité, pays ou projet.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid md:grid-cols-5 gap-3">
        <Stat label="Fiches totales" value={t.fiches} icon={AlertTriangle} color="#D4A017" testid="diag-kpi-total" />
        <Stat
          label="Critiques"
          value={data.by_gravite?.critique || 0}
          icon={Flame}
          color="#C0392B"
          testid="diag-kpi-critique"
        />
        <Stat
          label="Graves"
          value={data.by_gravite?.grave || 0}
          icon={AlertOctagon}
          color="#E67E22"
          testid="diag-kpi-grave"
        />
        <Stat
          label="Projets concernés"
          value={`${t.projects_with_diag}/${t.total_projects}`}
          icon={Folder}
          color="#1A3C5E"
          testid="diag-kpi-projects"
        />
        <Stat
          label="Impact fin. (Mrd USD)"
          value={((t.impact_financier_usd || 0) / 1_000_000_000).toFixed(2)}
          icon={DollarSign}
          color="#1B4332"
          testid="diag-kpi-impact"
        />
      </div>

      {isEmpty && (
        <div
          className="rap-card p-10 text-center"
          style={{ background: "rgba(212,160,23,0.04)" }}
          data-testid="diag-empty"
        >
          <Info size={32} style={{ color: "#D4A017" }} className="mx-auto mb-3" />
          <h3 className="font-display font-bold text-base mb-2">
            Aucune fiche diagnostic pour le moment
          </h3>
          <p className="text-xs opacity-70 max-w-md mx-auto mb-3">
            Pour générer des fiches, ouvrez un projet, lancez l'analyse juridique
            puis les fiches diagnostic. Elles seront agrégées ici automatiquement.
          </p>
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 text-sm font-semibold"
            style={{ color: "#D4A017" }}
          >
            Mes projets <ArrowRight size={14} />
          </Link>
        </div>
      )}

      {!isEmpty && (
        <>
          {/* Charts */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rap-card p-5" data-testid="diag-pie-gravite">
              <h2 className="font-display font-bold text-base mb-3">
                Répartition par gravité
              </h2>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={gravitePie}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                    >
                      {gravitePie.map((s) => (
                        <Cell key={s.key} fill={GRAV_COLOR[s.key]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rap-card p-5" data-testid="diag-top-anomalies">
              <h2 className="font-display font-bold text-base mb-3">
                Top 10 anomalies récurrentes
              </h2>
              {(data.top_anomalies || []).length > 0 ? (
                <div style={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={data.top_anomalies}
                      layout="vertical"
                      margin={{ left: 10, right: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={200}
                        tick={{ fontSize: 10 }}
                      />
                      <Tooltip />
                      <Bar dataKey="count" fill="#D4A017" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-xs opacity-60 text-center py-10">
                  Pas assez de données.
                </div>
              )}
            </div>
          </div>

          {/* Filterable list */}
          <div className="rap-card p-5" data-testid="diag-list">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="font-display font-bold text-base">
                Toutes les fiches ({filtered.length})
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <FilterSelect
                  value={filters.gravite}
                  onChange={(v) => setFilters({ ...filters, gravite: v })}
                  testid="diag-f-gravite"
                  options={[
                    ["all", "Toutes gravités"],
                    ["critique", "Critique"],
                    ["grave", "Grave"],
                    ["moderee", "Modérée"],
                    ["mineure", "Mineure"],
                  ]}
                />
                <FilterSelect
                  value={filters.priorite}
                  onChange={(v) => setFilters({ ...filters, priorite: v })}
                  testid="diag-f-priorite"
                  options={[
                    ["all", "Toutes priorités"],
                    ["urgent", "Urgent"],
                    ["court_terme", "Court terme"],
                    ["moyen_terme", "Moyen terme"],
                    ["long_terme", "Long terme"],
                  ]}
                />
                <FilterSelect
                  value={filters.country}
                  onChange={(v) => setFilters({ ...filters, country: v })}
                  testid="diag-f-country"
                  options={[
                    ["all", "Tous pays"],
                    ...(data.by_country || []).map((c) => [c.name, c.name]),
                  ]}
                />
                <FilterSelect
                  value={filters.project}
                  onChange={(v) => setFilters({ ...filters, project: v })}
                  testid="diag-f-project"
                  options={[
                    ["all", "Tous projets"],
                    ...projects.map((p) => [p.id, p.name]),
                  ]}
                />
                <div className="flex items-center gap-1 border rounded-sm px-2 py-1"
                  style={{ borderColor: "hsl(var(--border))" }}>
                  <Search size={12} className="opacity-60" />
                  <input
                    placeholder="Rechercher…"
                    value={filters.q}
                    onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                    className="bg-transparent text-xs outline-none w-40"
                    data-testid="diag-f-search"
                  />
                </div>
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="text-xs opacity-60 text-center py-10">
                Aucune fiche ne correspond aux filtres actifs.
              </div>
            ) : (
              <div className="space-y-2">
                {filtered.map((f) => (
                  <div
                    key={f.id}
                    className="p-3 border rounded-sm"
                    style={{ borderColor: "hsl(var(--border))" }}
                    data-testid={`diag-row-${f.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm uppercase"
                        style={{
                          background: `${GRAV_COLOR[f.gravite]}18`,
                          color: GRAV_COLOR[f.gravite],
                        }}
                      >
                        {GRAV_LABEL[f.gravite] || f.gravite}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">{f.anomalie}</div>
                        {f.qualification_juridique && (
                          <div className="text-xs opacity-75 mt-0.5">
                            {f.qualification_juridique}
                          </div>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-[10px] opacity-60 font-mono">
                          <Link
                            to={`/projects/${f.project_id}`}
                            className="hover:underline"
                            style={{ color: "#D4A017" }}
                          >
                            {f.project_name}
                          </Link>
                          <span>·</span>
                          <span>{f.project_country}</span>
                          <span>·</span>
                          <span className="flex items-center gap-1">
                            <Clock size={10} /> {PRIO_LABEL[f.priorite] || f.priorite}
                          </span>
                          {f.impact_financier_usd ? (
                            <>
                              <span>·</span>
                              <span>
                                {(Number(f.impact_financier_usd) / 1_000_000).toFixed(1)} M USD
                              </span>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="rap-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
        <Icon size={14} style={{ color }} />
      </div>
      <div className="font-mono text-2xl font-bold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, options, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-xs border rounded-sm bg-transparent px-2 py-1"
      style={{ borderColor: "hsl(var(--border))" }}
      data-testid={testid}
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>{label}</option>
      ))}
    </select>
  );
}
